import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import Clipboard from 'core/clipboard/Clipboard';
import SidebarBriefModal from 'core/modal/SidebarBriefModal';

import { buildRoute } from 'routes';
import ucwords from 'helpers/ucwords';
import { getFinmoPayout } from 'app/features/finmo-payouts/finmoPayoutSaga';
import { hideFinmoPayoutBriefDetails } from 'app/features/finmo-payouts/finmoPayoutSlice';
import FinmoUtils from 'utils/FinmoUtils';
import Modal from 'app/components/Modal';
import useSourceDetail from 'core/source-detail/useSourceDetail';
import SourceDetails from 'core/source-detail/SourceDetails';

function FinmoPayoutPreviewDetailsModal() {
  const dispatch = useDispatch();

  const navigate = useNavigate();

  const viewData = useMemo(
    () => [
      {
        label: 'Payout ID',
        get: ({ payout_id }) => <Clipboard content={payout_id} />,
      },
      {
        label: 'Beneficiary',
        get: ({ payout_beneficiary_param }) =>
          FinmoUtils.getPayoutBeneficiaryName(payout_beneficiary_param),
      },
      {
        label: 'Payout Method Name',
        data: 'payout_method_name',
      },
      {
        label: 'Description',
        data: 'description',
      },
      {
        label: 'Beneficiary Amount',
        data: 'beneficiary_amount',
      },
      {
        label: 'Beneficiary Currency',
        data: 'beneficiary_currency',
      },
      {
        label: 'Beneficiary Country',
        data: 'beneficiary_country',
      },
      {
        label: 'Payout Reference',
        data: 'payout_reference',
      },
      {
        label: 'Status',
        data: 'status',
      },
      {
        label: 'Pay ID',
        get: ({ payout_beneficiary_param }) => payout_beneficiary_param?.pay_id,
      },
    ],
    [],
  );

  const state = useSelector((state) => state.finmoPayouts.show);

  const { showBriefDetailModal, finmoPayoutId, isLoading, data } = state;

  useEffect(() => {
    if (finmoPayoutId) dispatch(getFinmoPayout(finmoPayoutId));
  }, [finmoPayoutId]);

  if (!showBriefDetailModal && !finmoPayoutId) return <></>;

  const viewDetailColumn = useSourceDetail([
    {
      title: '',
      items: [
        {
          label: 'Payout ID',
          accessorKey: 'payout_id',
          cell: ({ payout_id }) => <Clipboard content={payout_id} />,
        },
        {
          label: 'Beneficiary',
          accessorKey: 'payout_beneficiary_param',
          cell: ({ payout_beneficiary_param }) =>
            FinmoUtils.getPayoutBeneficiaryName(payout_beneficiary_param),
        },
        {
          label: 'Payout Method Name',
          accessorKey: 'payout_method_name',
        },
        {
          label: 'Description',
          accessorKey: 'description',
        },
        {
          label: 'Beneficiary Amount',
          accessorKey: 'beneficiary_amount',
        },
        {
          label: 'Beneficiary Currency',
          accessorKey: 'beneficiary_currency',
        },
        {
          label: 'Beneficiary Country',
          accessorKey: 'beneficiary_country',
        },
        {
          label: 'Payout Reference',
          accessorKey: 'payout_reference',
        },
        {
          label: 'Status',
          accessorKey: 'status',
        },
        {
          label: 'Pay ID',
          accessorKey: 'payout_beneficiary_param.pay_id',
          cell: ({ payout_beneficiary_param }) => payout_beneficiary_param?.pay_id || '-',
        },
      ],
    },
  ]);

  return (
    <Modal
      open={showBriefDetailModal ?? false}
      onClose={() => dispatch(hideFinmoPayoutBriefDetails())}
      title="Finmo Payout Details"
      handleFullDetails={() => (
        dispatch(hideFinmoPayoutBriefDetails()),
        navigate(buildRoute('finmo-payouts.show', finmoPayoutId))
      )}
    >
      <SourceDetails
        showContainerDesign={false}
        definition={viewDetailColumn}
        data={data}
        isLoading={isLoading}
      />
    </Modal>
  );
}

export default FinmoPayoutPreviewDetailsModal;
